package com.evoapps.evoshift.domain.usecase.shift

import com.evoapps.evoshift.domain.model.DayClosedException
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import com.evoapps.evoshift.domain.usecase.finance.CalculateDailyRecordUseCase

/** Which shifts a copy of the planned times would change, and which already have their own times. */
data class PlannedTimesCopy(
    /** Assigned shifts without any actual time: they get the planned times. */
    val empty: List<Shift>,
    /** Assigned shifts already clocked (in, or in and out): kept unless overwriting is confirmed. */
    val clocked: List<Shift>
) {
    val isEmpty: Boolean get() = empty.isEmpty() && clocked.isEmpty()

    /** The shifts a copy writes: the empty ones, and the clocked ones too when [overwrite]. */
    fun targets(overwrite: Boolean): List<Shift> = if (overwrite) empty + clocked else empty

    companion object {
        /**
         * The assigned shifts of [date] among [shifts] (only [shiftIds] when given), split into those
         * without actual times and those already clocked. Open shifts nobody worked are left out
         * (SH-128: an open shift somebody was recorded on counts as theirs).
         */
        fun of(shifts: List<Shift>, date: String, shiftIds: Set<String>? = null): PlannedTimesCopy {
            val chosen = shifts.filter { it.date == date && it.workerId != null && (shiftIds == null || it.id in shiftIds) }
            val (clocked, empty) = chosen.partition { it.hasActualTime }
            return PlannedTimesCopy(empty.sortedBy { it.plannedTime.start }, clocked.sortedBy { it.plannedTime.start })
        }
    }
}

/** A shift with any actual time recorded (clocked in, or in and out). */
val Shift.hasActualTime: Boolean
    get() = actualTime?.let { it.start.isNotEmpty() || it.end.isNotEmpty() } == true

/** The shift with its planned times as the actual ones. */
fun Shift.withPlannedAsActual(): Shift = copy(actualTime = plannedTime)

/**
 * SH-46, as "Rejestr Godzin" did: the manager copies the planned times into the actual ones, for a
 * whole day or a single shift, where people worked as rostered but did not clock. Times already
 * clocked are kept unless [overwrite] (the screen asks first). A closed (locked) day is final
 * ([DayClosedException]). The copied shifts are repriced from the new times (best effort, needs
 * the pay rates) and the day's record is recomputed, so the actual labour cost follows.
 */
class CopyPlannedTimesToActualUseCase(
    private val shiftRepository: ShiftRepository,
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase,
    private val calculateShiftCost: CalculateShiftCostUseCase? = null
) {
    /** What was copied, and the day's record after it (null if it could not be recomputed). */
    data class Outcome(val copied: List<Shift>, val record: DailyRecord?)

    suspend operator fun invoke(
        workplaceId: String,
        rosterId: String,
        date: String,
        shiftIds: Set<String>? = null,
        overwrite: Boolean = false,
        editorUserId: String
    ): Result<Outcome> = runCatching {
        val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrThrow().firstOrNull { it.date == date }
        if (stored?.isLocked == true) throw DayClosedException(date)
        val shifts = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
        val targets = PlannedTimesCopy.of(shifts, date, shiftIds).targets(overwrite)
        targets.forEach { shift ->
            shiftRepository.updateActualTime(shift.id, shift.plannedTime, editorUserId).getOrThrow()
        }
        if (targets.isEmpty()) return@runCatching Outcome(emptyList(), stored)
        // Priced from the clocked times now (a financial manager's pay rates; skipped otherwise).
        targets.forEach { calculateShiftCost?.invoke(it.id) }
        val record = calculateDailyRecordUseCase.recompute(workplaceId, rosterId, date) { it }.getOrNull()
        record?.let { financialRepository.saveDailyRecord(it).getOrThrow() }
        Outcome(targets.map { it.withPlannedAsActual() }, record)
    }
}
